/**
 * Where HISCUS works.
 *
 * "Where we work" is a top-level route on every serious reference site studied
 * (BRAC, IRC, Mercy Corps, WaterAid, PHASE Nepal), funders and institutional
 * visitors look for geography before anything else.
 *
 * Only places named in HISCUS's own project reporting appear here, and each one
 * lists the specific work recorded there. No coverage is extrapolated from a
 * single event, and no district is claimed because a neighbouring one appears.
 */
export type Location = {
  id: string;
  name: string;
  region: string;
  summary: string;
  work: { label: string; detail: string; year: string }[];
  pillars: string[];
};

export const locations: Location[] = [
  {
    id: "bethanchowk",
    name: "Bethanchowk",
    region: "Kavrepalanchok",
    summary:
      "The clearest example of how HISCUS works: three different kinds of support in the same community inside nine months, ending with infrastructure the community maintains itself.",
    work: [
      {
        label: "Health post support",
        detail: "Supplies and support delivered to the local health post on 1 January 2025.",
        year: "2025",
      },
      {
        label: "Empowering with Dignity session",
        detail: "18 women took part in menstrual health education and reusable pad making, 28 January 2025.",
        year: "2025",
      },
      {
        label: "Community water tank",
        detail:
          "Built October 2024 – 9 July 2025, restoring reliable water access for at least 60 households.",
        year: "2025",
      },
    ],
    pillars: ["Women & Girls", "Health, Relief & Community Resilience"],
  },
  {
    id: "sarangkot",
    name: "Sarangkot",
    region: "Kaski",
    summary:
      "Home to the Sarangkot Nestling Home, HISCUS's longest continuous commitment, alongside the access infrastructure the village asked for.",
    work: [
      {
        label: "Sarangkot Nestling Home",
        detail:
          "Completed 13 April 2018 with The Nestling Trust UK. Eleven girls supported as reported for 2025.",
        year: "2018",
      },
      {
        label: "Community access path",
        detail: "A 700-metre shortcut path built for Sarangkot villagers.",
        year: "Completion year to be confirmed",
      },
    ],
    pillars: ["Children & Education", "Health, Relief & Community Resilience"],
  },
  {
    id: "thapathali",
    name: "Thapathali",
    region: "Kathmandu",
    summary:
      "Flood response on the Bagmati riverside, delivered within days and leaving water storage behind.",
    work: [
      {
        label: "Flood response",
        detail:
          "Clothing support for affected children and a community water-storage tank, 8–9 October 2024.",
        year: "2024",
      },
    ],
    pillars: ["Health, Relief & Community Resilience"],
  },
  {
    id: "bhaktapur",
    name: "Bhaktapur",
    region: "Bhaktapur",
    summary:
      "Psychosocial wellbeing work alongside protective infrastructure for an older people's home.",
    work: [
      {
        label: "Psychosocial wellbeing session",
        detail: "20 participants joined a wellbeing and stress-management session on 17 March 2025.",
        year: "2025",
      },
      {
        label: "Protective wall",
        detail: "Protective-wall work for the Bhaktapur Old Age Home.",
        year: "Year to be confirmed",
      },
    ],
    pillars: ["Women & Girls", "Health, Relief & Community Resilience"],
  },
  {
    id: "ropeway-sadak",
    name: "Ropeway Sadak",
    // HISCUS names the place but not its district, and "Ropeway Sadak" exists in
    // more than one part of Nepal. Not guessed, see CONTENT_CONFIRMATION.md.
    region: "District to be confirmed",
    summary: "The most recent documented Empowering with Dignity session.",
    work: [
      {
        label: "Empowering with Dignity session",
        detail: "25 participants joined menstrual health education and pad making, 13 June 2025.",
        year: "2025",
      },
    ],
    pillars: ["Women & Girls"],
  },
  {
    id: "pokhara",
    name: "Pokhara",
    region: "Kaski",
    summary: "Supervised learning outside school hours, supporting children to stay in education.",
    work: [
      {
        label: "Before and after-school learning",
        detail: "Supervised study and activity sessions outside school hours.",
        year: "Ongoing",
      },
    ],
    pillars: ["Children & Education"],
  },
  {
    id: "chitwan",
    name: "Chitwan",
    region: "Chitwan",
    summary: "Supervised learning outside school hours, supporting children to stay in education.",
    work: [
      {
        label: "Before and after-school learning",
        detail: "Supervised study and activity sessions outside school hours.",
        year: "Ongoing",
      },
    ],
    pillars: ["Children & Education"],
  },
];
